"use client";

import { ChartBar as BarChart3, Search, Target, TrendingUp, Wrench } from 'lucide-react';
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Methodology() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.12
  });
  const steps = [{
    number: "01",
    icon: Search,
    title: "Discovery & Audit",
    description: "I understand the business, website, market, competitors, existing performance and opportunities."
  }, {
    number: "02",
    icon: Target,
    title: "Strategy",
    description: "I build a prioritised SEO roadmap based on business objectives and realistic organic-search opportunities."
  }, {
    number: "03",
    icon: Wrench,
    title: "Implementation",
    description: "I execute or support technical, on-page, content, authority and search-visibility improvements."
  }, {
    number: "04",
    icon: BarChart3,
    title: "Measurement & Optimisation",
    description: "I track meaningful performance, diagnose changes and continually improve the strategy."
  }, {
    number: "05",
    icon: TrendingUp,
    title: "Growth",
    description: "I scale what works and identify new organic-search opportunities."
  }];
  return <section data-section-id="15729" id="my-seo-methodology" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div ref={headerRef} className="mx-auto max-w-[760px] text-center transition-all duration-[640ms] ease-out" style={{
        opacity: headerVisible ? 1 : 0,
        transform: headerVisible ? "translateY(0)" : "translateY(16px)"
      }}>
          <div className="flex flex-col items-center">
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-foreground">
              My Approach
            </span>
            <span aria-hidden="true" className="mt-3 block h-px w-12" style={{
            backgroundColor: "var(--primary)"
          }} />
          </div>
          <h2 className="mt-8 font-serif text-[clamp(1.8125rem,2.8vw,2.375rem)] font-normal leading-[1.08] text-foreground" style={{
          letterSpacing: "-0.018em"
        }}>
            Five steps to{" "}
            <em className="font-serif font-normal italic" style={{
            color: "var(--accent)"
          }}>
              sustainable
            </em>{" "}
            search growth.
          </h2>
        </div>

        <div ref={stepsRef} className="mt-16">
          <div className="hidden md:block">
            <div className="relative grid grid-cols-5 gap-6">
              <div aria-hidden="true" className="pointer-events-none absolute left-0 right-0 z-0" style={{
              top: "0.95rem",
              height: "1px",
              backgroundColor: "var(--border)"
            }} />
              <div aria-hidden="true" className="pointer-events-none absolute z-10" style={{
              top: "calc(0.95rem - 3.5px)",
              right: "-4px",
              width: "8px",
              height: "8px",
              borderRadius: "9999px",
              backgroundColor: "var(--accent)"
            }} />

              {steps.map((step, i) => {
              const Icon = step.icon;
              return <div key={step.number} data-index={i} className="relative z-[1] transition-all ease-out" style={{
                transitionDuration: "640ms",
                transitionDelay: `${i * 120}ms`,
                opacity: stepsVisible ? 1 : 0,
                transform: stepsVisible ? "translateY(0)" : "translateY(16px)"
              }}>
                    <div className="flex justify-start">
                      <span className="bg-background pr-3 font-mono text-xl font-medium text-foreground" style={{
                    lineHeight: 1
                  }}>
                        {step.number}
                      </span>
                    </div>

                    <div aria-hidden="true" className="mt-4 h-px w-full" style={{
                  backgroundColor: "var(--border)"
                }} />

                    <div className="mt-5">
                      <Icon className="h-6 w-6" strokeWidth={1.5} style={{
                    color: "var(--primary)"
                  }} />
                    </div>

                    <h3 className="mt-4 font-default text-lg font-semibold text-foreground" style={{
                  letterSpacing: "-0.005em",
                  lineHeight: 1.35
                }}>
                      {step.title}
                    </h3>

                    <p className="mt-2.5 font-default text-[1.0625rem] text-muted-foreground" style={{
                  lineHeight: 1.6,
                  maxWidth: "36ch"
                }}>
                      {step.description}
                    </p>
                  </div>;
            })}
            </div>
          </div>

          <div className="md:hidden">
            <div className="relative pl-8">
              <div aria-hidden="true" className="absolute bottom-0 left-0 top-0 w-px" style={{
              backgroundColor: "var(--border)"
            }} />
              <div aria-hidden="true" className="absolute" style={{
              bottom: "-4px",
              left: "-3.5px",
              width: "8px",
              height: "8px",
              borderRadius: "9999px",
              backgroundColor: "var(--accent)"
            }} />

              <div className="flex flex-col gap-y-10">
                {steps.map((step, i) => {
                const Icon = step.icon;
                return <div key={step.number} data-index={i} className="relative transition-all ease-out" style={{
                  transitionDuration: "640ms",
                  transitionDelay: `${i * 120}ms`,
                  opacity: stepsVisible ? 1 : 0,
                  transform: stepsVisible ? "translateY(0)" : "translateY(16px)"
                }}>
                      <span className="absolute bg-background px-2 font-mono text-xl font-medium text-foreground" style={{
                    left: "-2.25rem",
                    top: "-0.25rem",
                    lineHeight: 1
                  }}>
                        {step.number}
                      </span>

                      <div>
                        <Icon className="h-6 w-6" strokeWidth={1.5} style={{
                      color: "var(--primary)"
                    }} />
                      </div>

                      <h3 className="mt-4 font-default text-lg font-semibold text-foreground" style={{
                    letterSpacing: "-0.005em",
                    lineHeight: 1.35
                  }}>
                        {step.title}
                      </h3>

                      <p className="mt-2.5 font-default text-[1.0625rem] text-muted-foreground" style={{
                    lineHeight: 1.6
                  }}>
                        {step.description}
                      </p>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}